import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Sidebar from '../components/common/Sidebar/Sidebar';
import Navbar from '../components/common/Navbar/Navbar';
import StudentDashboard from '../pages/student/StudentDashboard';
import StudentProjects from '../pages/student/StudentProjects';
import StudentOnboarding from '../pages/student/StudentOnboarding';
import StudentClasses from '../pages/student/StudentClasses';
import StudentBookSession from '../pages/student/StudentBookSession';
import StudentSelectTime from '../pages/student/StudentSelectTime';
import StudentAssignments from '../pages/student/StudentAssignments';
import StudentBOM from '../pages/student/StudentBOM';
import StudentCompetitions from '../pages/student/StudentCompetitions';
import StudentCompetitionApplication from '../pages/student/StudentCompetitionApplication';
import StudentCompetitionOverview from '../pages/student/StudentCompetitionOverview';
import StudentFindCompetitions from '../pages/student/StudentFindCompetitions';
import StudentDisputes from '../pages/student/StudentDisputes';
import ProjectDetails from '../pages/common/ProjectDetails';
import { getMyStudentProfile } from '../services/studentService';
import styles from './Routes.module.css';

const StudentRoutes = () => {
  // Null while we are still asking. The tab is rendered only on an explicit
  // true, so a slow or failed profile call shows no tab rather than flashing
  // one in and then taking it away.
  const [compMatcherEnabled, setCompMatcherEnabled] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getMyStudentProfile()
      .then((profile) => {
        if (!cancelled) setCompMatcherEnabled(Boolean(profile?.comp_matcher_enabled));
      })
      .catch(() => {
        // A student still in onboarding has no profile row yet. Treated as
        // "no access" rather than as an error — the rest of the sidebar works.
        if (!cancelled) setCompMatcherEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const menuItems = [
    { label: 'Dashboard', path: '/student/dashboard', icon: 'dashboard' },
    { label: 'My Projects', path: '/student/projects', icon: 'projects' },
    { label: 'Classes', path: '/student/classes', icon: 'classes' },
    // matchNested: stays highlighted on the mentor time picker, /student/book-session/<id>.
    { label: 'Book a Session', path: '/student/book-session', icon: 'calendar', matchNested: true },
    { label: 'Assignments', path: '/student/assignments', icon: 'assignments' },
    { label: 'Bill of Materials', path: '/student/bom', icon: 'materials' },
    // Sits directly above the existing Competitions tab: this is where a
    // competition is found, that is where the work on it is submitted.
    ...(compMatcherEnabled
      ? [{ label: 'Find Competitions', path: '/student/find-competitions', icon: 'competitions' }]
      : []),
    // matchNested: stays highlighted on /student/competitions/<id>/... too.
    { label: 'Competitions', path: '/student/competitions', icon: 'competitions', matchNested: true },
    { label: 'Disputes', path: '/student/disputes', icon: 'disputes' }
  ];

  return (
    <div className={styles.layout}>
      <Sidebar menuItems={menuItems} role="student" />
      <div className={styles.mainContent}>
        <Navbar title="COSMIC Student" />
        <div className={styles.content}>
          <Routes>
            <Route path="onboarding" element={<StudentOnboarding />} />
            <Route path="dashboard" element={<StudentDashboard />} />
            <Route path="projects" element={<StudentProjects />} />
            <Route path="projects/:projectId" element={<ProjectDetails />} />
            <Route path="classes" element={<StudentClasses />} />
            <Route path="book-session" element={<StudentBookSession />} />
            <Route path="book-session/:mentorId" element={<StudentSelectTime />} />
            <Route path="assignments" element={<StudentAssignments />} />
            <Route path="bom" element={<StudentBOM />} />
            {/* Hiding the tab is not the gate. A student who types the URL,
                or keeps a bookmark after access is revoked, lands back on the
                dashboard — and the API refuses them independently. */}
            <Route
              path="find-competitions"
              element={
                compMatcherEnabled
                  ? <StudentFindCompetitions />
                  : <Navigate to="/student/dashboard" replace />
              }
            />
            <Route path="competitions" element={<StudentCompetitions />} />
            <Route path="competitions/:templateId" element={<StudentCompetitionOverview />} />
            <Route path="competitions/:templateId/application" element={<StudentCompetitionApplication />} />
            <Route path="disputes" element={<StudentDisputes />} />
            <Route path="/" element={<Navigate to="dashboard" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default StudentRoutes;

